/**
 * T200 — Durcharbeite API client.
 *
 * Wraps the routes mounted at /api/v1/expenses/durcharbeite:
 *   GET  /queue                 → paginated review queue
 *   GET  /:id                   → expense + navigation + inQueue flag
 *   POST /:id/save-and-next     → apply patch, promote out of review,
 *                                 return next id
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { durcharbeiteKeys, expenseKeys } from './keys';
import { useActiveTenantId } from '../../hooks/useTenantContext';
import type {
  ExpenseDetailDto,
  UpdateExpenseInput,
} from '@armard/shared';

export interface DurcharbeiteQueueItemDto {
  id: string;
  supplierId: string | null;
  supplierInvoiceNumber: string | null;
  invoiceDate: string;
  grossTotal: string;
  status: string;
  trustLevel: 'validated' | 'review' | 'manual';
  updatedAt: string;
}

export interface DurcharbeiteQueuePage {
  items: DurcharbeiteQueueItemDto[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface DurcharbeiteNavigationDto {
  prevId: string | null;
  nextId: string | null;
  queuePosition: number;
  queueTotal: number;
}

export interface DurcharbeiteItemDto {
  expense: ExpenseDetailDto;
  navigation: DurcharbeiteNavigationDto;
  inQueue: boolean;
}

export interface SaveAndNextResult {
  saved: ExpenseDetailDto;
  nextId: string | null;
  queueRemaining: number;
}

export const durcharbeiteApi = {
  queue: (q: { page?: number; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (q.page != null) params.set('page', String(q.page));
    if (q.limit != null) params.set('limit', String(q.limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return api.get<DurcharbeiteQueuePage>(`/expenses/durcharbeite/queue${suffix}`);
  },
  get: (id: string) =>
    api.get<DurcharbeiteItemDto>(`/expenses/durcharbeite/${id}`),
  saveAndNext: (
    id: string,
    payload: { patch: UpdateExpenseInput; promoteTo?: 'manual' | 'validated' }
  ) =>
    api.post<SaveAndNextResult>(
      `/expenses/durcharbeite/${id}/save-and-next`,
      payload
    ),
};

export function useDurcharbeiteQueue(q: { page?: number; limit?: number } = {}) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: durcharbeiteKeys.queue(tenantId, q),
    queryFn: () => durcharbeiteApi.queue(q),
    enabled: tenantId !== null,
  });
}

export function useDurcharbeiteItem(id: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: durcharbeiteKeys.item(tenantId, id!),
    queryFn: () => durcharbeiteApi.get(id!),
    enabled: !!id && tenantId !== null,
  });
}

export function useDurcharbeiteSaveAndNext() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (args: {
      id: string;
      patch: UpdateExpenseInput;
      promoteTo?: 'manual' | 'validated';
    }) =>
      durcharbeiteApi.saveAndNext(args.id, {
        patch: args.patch,
        promoteTo: args.promoteTo,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: durcharbeiteKeys.queues(tenantId) });
      qc.invalidateQueries({ queryKey: durcharbeiteKeys.items(tenantId) });
      qc.invalidateQueries({ queryKey: expenseKeys.detail(tenantId, vars.id) });
      qc.invalidateQueries({ queryKey: expenseKeys.all(tenantId) });
    },
  });
}
